import React, { useState, useRef } from 'react';
import {
  Box,
  Heading,
  Button,
  HStack,
  VStack,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  Card,
  CardBody,
  Flex,
  Spacer,
  InputGroup,
  InputLeftElement,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useToast,
  Spinner,
  Center,
  useBreakpointValue,
  Divider,
} from '@chakra-ui/react';
import {
  AddIcon,
  SearchIcon,
  EditIcon,
  DeleteIcon,
  ViewIcon,
  ChevronDownIcon
} from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { Event, EventStatus, EventType } from '../types/api';
import { useAuth } from '../contexts/AuthContext';
import { isFrontDesk } from '../utils/access';

// Mobile Event Card Component
interface EventCardProps {
  event: Event;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canEdit: boolean;
  isAdmin: boolean;
  frontDesk?: boolean;
  onCheckin?: () => void;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  onView,
  onEdit,
  onDelete,
  canEdit,
  isAdmin,
  frontDesk,
  onCheckin,
}) => {
  const getStatusColor = (status: EventStatus) => {
    switch (status) {
      case 'published': return 'green';
      case 'draft': return 'gray';
      case 'cancelled': return 'red';
      case 'completed': return 'blue';
      case 'postponed': return 'orange';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status: EventStatus) => {
    switch (status) {
      case 'published': return 'Publié';
      case 'draft': return 'Brouillon';
      case 'cancelled': return 'Annulé';
      case 'completed': return 'Terminé';
      case 'postponed': return 'Reporté';
      default: return status;
    }
  };

  const getTypeLabel = (type: EventType) => {
    switch (type) {
      case 'press_conference': return 'Conférence de presse';
      case 'employee_event': return 'Événement employés';
      case 'board_meeting': return 'Réunion du conseil';
      case 'general_assembly': return 'Assemblée générale';
      case 'vip_event': return 'Événement VIP';
      case 'training': return 'Formation';
      case 'ceremony': return 'Cérémonie';
      case 'other': return 'Autre';
      default: return type;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card>
      <CardBody>
        <VStack align="stretch" spacing={3}>
          {/* Header */}
          <Flex justify="space-between" align="start">
            <VStack align="start" spacing={1} flex="1">
              <Text fontWeight="bold" fontSize="md">
                {event.title}
              </Text>
              <Badge colorScheme={getStatusColor(event.status)}>
                {getStatusLabel(event.status)}
              </Badge>
            </VStack>
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<ChevronDownIcon />}
                variant="ghost"
                size="sm"
              />
              <MenuList>
                <MenuItem icon={<ViewIcon />} onClick={onView}>
                  Voir
                </MenuItem>
                {canEdit && (
                  <MenuItem icon={<EditIcon />} onClick={onEdit}>
                    Modifier
                  </MenuItem>
                )}
                {isAdmin && (
                  <MenuItem icon={<DeleteIcon />} color="red.600" onClick={onDelete}>
                    Supprimer
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          </Flex>

          <Divider />

          {/* Details */}
          <VStack align="start" spacing={2} fontSize="sm">
            <Text color="gray.600">
              📅 {formatDate(event.event_date)}
            </Text>
            {event.location && (
              <Text color="gray.600">
                📍 {event.location}
              </Text>
            )}
            <Badge colorScheme="purple" variant="subtle">
              {getTypeLabel(event.event_type)}
            </Badge>
          </VStack>

          {frontDesk && onCheckin && (
            <Button colorScheme="blue" w="full" onClick={onCheckin} data-testid="event-checkin-button">
              Check-in
            </Button>
          )}

          <Divider />

          {/* Stats */}
          <HStack spacing={4} justify="space-between">
            <VStack align="start" spacing={0}>
              <Text fontSize="xs" color="gray.500">Invités</Text>
              <Text fontWeight="bold">
                {event.current_attendees}
                {event.max_attendees && <Text as="span" color="gray.500"> / {event.max_attendees}</Text>}
              </Text>
            </VStack>
            <VStack align="start" spacing={0}>
              <Text fontSize="xs" color="gray.500">VIPs</Text>
              <Text fontWeight="bold">{event.vip_count || 0}</Text>
            </VStack>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );
};

const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Detect screen size
  const isMobile = useBreakpointValue({ base: true, md: false });

  // State for filters and pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<EventStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<EventType | ''>('');
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);

  // Fetch events
  const { data: eventsData, isLoading, error } = useQuery({
    queryKey: ['events', page, pageSize, searchQuery, statusFilter, typeFilter],
    queryFn: () => apiService.getEvents({
      page,
      page_size: pageSize,
      q: searchQuery || undefined,
      status: statusFilter || undefined,
      event_type: typeFilter || undefined,
      with_stats: true,
      sort_by: 'event_date',
      sort_order: 'asc'
    }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (eventId: number) => apiService.deleteEvent(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast({
        title: 'Événement supprimé',
        description: 'L\'événement a été supprimé avec succès',
        status: 'success',
        duration: 3000,
      });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.message || 'Erreur lors de la suppression',
        status: 'error',
        duration: 5000,
      });
    },
  });

  const handleDeleteEvent = (event: Event) => {
    setEventToDelete(event);
    onOpen();
  };

  const confirmDelete = () => {
    if (eventToDelete) {
      deleteMutation.mutate(eventToDelete.id);
    }
  };

  const getStatusColor = (status: EventStatus) => {
    switch (status) {
      case 'published': return 'green';
      case 'draft': return 'gray';
      case 'cancelled': return 'red';
      case 'completed': return 'blue';
      case 'postponed': return 'orange';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status: EventStatus) => {
    switch (status) {
      case 'published': return 'Publié';
      case 'draft': return 'Brouillon';
      case 'cancelled': return 'Annulé';
      case 'completed': return 'Terminé';
      case 'postponed': return 'Reporté';
      default: return status;
    }
  };

  const getTypeLabel = (type: EventType) => {
    switch (type) {
      case 'press_conference': return 'Conférence de presse';
      case 'employee_event': return 'Événement employés';
      case 'board_meeting': return 'Réunion du conseil';
      case 'general_assembly': return 'Assemblée générale';
      case 'vip_event': return 'Événement VIP';
      case 'training': return 'Formation';
      case 'ceremony': return 'Cérémonie';
      case 'other': return 'Autre';
      default: return type;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <Center py={10}>
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  if (error) {
    return (
      <Box>
        <Text color="red.500">Erreur lors du chargement des événements</Text>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header - Responsive */}
      <Flex 
        mb={6} 
        align={{ base: 'stretch', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={{ base: 3, md: 0 }}
      >
        <Heading size={{ base: 'md', md: 'lg' }}>Événements</Heading>
        <Spacer display={{ base: 'none', md: 'block' }} />
        {user?.can_create_events && (
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={() => navigate('/events/create')}
            size={{ base: 'md', md: 'md' }}
            w={{ base: 'full', md: 'auto' }}
          >
            Nouvel événement
          </Button>
        )}
      </Flex>

      {/* Filters - Responsive */}
      <Card mb={6}>
        <CardBody>
          {/* Mobile: Vertical Stack */}
          <VStack 
            spacing={3} 
            align="stretch"
            display={{ base: 'flex', md: 'none' }}
          >
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="md"
              />
            </InputGroup>

            <Select
              placeholder="Tous les statuts"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as EventStatus)}
              size="md"
            >
              <option value="draft">Brouillon</option>
              <option value="published">Publié</option>
              <option value="completed">Terminé</option>
              <option value="cancelled">Annulé</option>
              <option value="postponed">Reporté</option>
            </Select>

            <Select
              placeholder="Tous les types"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as EventType)}
              size="md"
            >
              <option value="press_conference">Conférence de presse</option>
              <option value="vip_event">Événement VIP</option>
              <option value="training">Formation</option>
              <option value="other">Autre</option>
            </Select>

            {(searchQuery || statusFilter || typeFilter) && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('');
                  setTypeFilter('');
                  setPage(1);
                }}
                size="sm"
              >
                Réinitialiser
              </Button>
            )}
          </VStack>

          {/* Desktop: Horizontal Stack */}
          <HStack 
            spacing={4} 
            wrap="wrap"
            display={{ base: 'none', md: 'flex' }}
          >
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Rechercher des événements..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>

            <Select
              placeholder="Tous les statuts"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as EventStatus)}
              maxW="200px"
            >
              <option value="draft">Brouillon</option>
              <option value="published">Publié</option>
              <option value="completed">Terminé</option>
              <option value="cancelled">Annulé</option>
              <option value="postponed">Reporté</option>
            </Select>

            <Select
              placeholder="Tous les types"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as EventType)}
              maxW="200px"
            >
              <option value="press_conference">Conférence de presse</option>
              <option value="vip_event">Événement VIP</option>
              <option value="training">Formation</option>
              <option value="other">Autre</option>
            </Select>

            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('');
                setTypeFilter('');
                setPage(1);
              }}
            >
              Réinitialiser
            </Button>
          </HStack>
        </CardBody>
      </Card>

      {/* Events List - Mobile Cards / Desktop Table */}
      {isMobile ? (
        // Mobile: Card View
        <VStack spacing={3} align="stretch">
          {eventsData?.items.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onView={() => navigate(`/events/${event.id}`)}
              onEdit={() => navigate(`/events/${event.id}/edit`)}
              onDelete={() => handleDeleteEvent(event)}
              canEdit={!!user?.can_create_events}
              isAdmin={user?.role === 'admin'}
              frontDesk={isFrontDesk(user?.role)}
              onCheckin={() => navigate(`/events/${event.id}`)}
            />
          ))}
          {eventsData?.items.length === 0 && (
            <Box p={8} textAlign="center">
              <Text color="gray.500">Aucun événement trouvé</Text>
            </Box>
          )}
        </VStack>
      ) : (
        // Desktop: Table View
        <Card>
          <CardBody p={0}>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Titre</Th>
                  <Th>Type</Th>
                  <Th>Date</Th>
                  <Th>Statut</Th>
                  <Th>Invités</Th>
                  <Th>VIPs</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {eventsData?.items.map((event) => (
                  <Tr key={event.id}>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">{event.title}</Text>
                        {event.location && (
                          <Text fontSize="sm" color="gray.600">
                            📍 {event.location}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <Badge colorScheme="purple" variant="subtle">
                        {getTypeLabel(event.event_type)}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {formatDate(event.event_date)}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(event.status)}>
                        {getStatusLabel(event.status)}
                      </Badge>
                    </Td>
                    <Td>
                      <Text>{event.current_attendees}</Text>
                      {event.max_attendees && (
                        <Text fontSize="sm" color="gray.600">
                          / {event.max_attendees}
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <Text>{event.vip_count || 0}</Text>
                    </Td>
                    <Td>
                      {isFrontDesk(user?.role) && (
                        <Button
                          size="sm"
                          colorScheme="blue"
                          mr={2}
                          onClick={() => navigate(`/events/${event.id}`)}
                          data-testid="event-checkin-button"
                        >
                          Check-in
                        </Button>
                      )}
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<ChevronDownIcon />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          <MenuItem
                            icon={<ViewIcon />}
                            onClick={() => navigate(`/events/${event.id}`)}
                          >
                            Voir les détails
                          </MenuItem>
                          {user?.can_create_events && (
                            <MenuItem
                              icon={<EditIcon />}
                              onClick={() => navigate(`/events/${event.id}/edit`)}
                            >
                              Modifier
                            </MenuItem>
                          )}
                          {user?.role === 'admin' && (
                            <MenuItem
                              icon={<DeleteIcon />}
                              color="red.600"
                              onClick={() => handleDeleteEvent(event)}
                            >
                              Supprimer
                            </MenuItem>
                          )}
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            {eventsData?.items.length === 0 && (
              <Box p={8} textAlign="center">
                <Text color="gray.500">Aucun événement trouvé</Text>
              </Box>
            )}
          </CardBody>
        </Card>
      )}

      {/* Pagination */}
      {eventsData && eventsData.total_pages > 1 && (
        <Flex justify="center" mt={6}>
          <HStack>
            <Button
              size="sm"
              isDisabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Précédent
            </Button>
            <Text px={4} fontSize="sm">
              Page {page} sur {eventsData.total_pages}
            </Text>
            <Button
              size="sm"
              isDisabled={page === eventsData.total_pages}
              onClick={() => setPage(page + 1)}
            >
              Suivant
            </Button>
          </HStack>
        </Flex>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent mx={{ base: 4, md: 0 }}>
            <AlertDialogHeader fontSize={{ base: 'md', md: 'lg' }}>
              Supprimer l'événement
            </AlertDialogHeader>
            <AlertDialogBody fontSize={{ base: 'sm', md: 'md' }}>
              Êtes-vous sûr de vouloir supprimer l'événement "{eventToDelete?.title}" ?
              Cette action est irréversible.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} size={{ base: 'sm', md: 'md' }}>
                Annuler
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                ml={3}
                isLoading={deleteMutation.isPending}
                size={{ base: 'sm', md: 'md' }}
              >
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default EventsPage;